import type { TOC } from '@ember/component/template-only'
import { TwoDatePicker } from 'teacher-ui-client/components/dates/two-date-picker'
import { TwoDatePickerInput } from 'teacher-ui-client/components/dates/two-date-picker/input'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { InputText } from 'teacher-ui-client/components/base/input-text'
import { RadioButtonGroup } from 'teacher-ui-client/components/base/radio-button-group'
import { t } from 'ember-intl'
import { uniqueId } from '@ember/helper'
import { and } from 'ember-truth-helpers'
import { on } from '@ember/modifier'
import { array } from '@ember/helper'
import { hash } from '@ember/helper'
import type { ThemeKeys } from 'teacher-ui-client/utils/themes'

interface Signature {
  Element: HTMLDivElement
  Args: {
    startDate: Date
    endDate: Date
    startDateDisabled?: boolean
    startDateDisabledText?: string
    themeKey?: ThemeKeys
    onDateSelect?: ({ startDate, endDate }: { startDate: Date; endDate?: Date | null }) => unknown
  }
}

export const BookLibraryDateRange: TOC<Signature> = <template>
  <div class="flex flex-wrap gap-4" ...attributes>
    {{#let (uniqueId) as |uniqueId|}}
      <TwoDatePicker @startDate={{@startDate}} @endDate={{@endDate}} @onDateSelect={{@onDateSelect}} as |twoDate|>
        <fieldset class="space-y-1">
          <label for="{{uniqueId}}-start" class="block">{{t "bookLibrary.components.dateRange.startDate"}}</label>
          <TwoDatePickerInput
            id="{{uniqueId}}-start"
            name="start-date"
            disabled={{@startDateDisabled}}
            @date={{@startDate}}
            @onDateSelect={{twoDate.start.onDateSelect}}
            @min={{twoDate.start.min}}
            @themeKey={{@themeKey}}
          />
          {{#if (and @startDateDisabled @startDateDisabledText)}}
            <Tooltip @text={{@startDateDisabledText}} />
          {{/if}}
        </fieldset>
        <fieldset class="space-y-1">
          <label for="{{uniqueId}}-duration-days" class="block">{{t
              "bookLibrary.components.dateRange.duration"
            }}</label>
          <InputText
            id="{{uniqueId}}-duration-days"
            name="duration-days"
            class="w-16 text-center"
            type="number"
            value={{twoDate.durationDays}}
            @themeKey={{@themeKey}}
            {{on "input" twoDate.setAbsoluteDays}}
          />
          <span>{{t "bookLibrary.components.dateRange.daysOr"}}</span>
          <RadioButtonGroup
            class="inline-flex"
            @name="duration-days"
            @buttons={{array
              (hash value=7 text=(t "bookLibrary.components.dateRange.addTimeValue" days=7))
              (hash value=14 text=(t "bookLibrary.components.dateRange.addTimeValue" days=14))
              (hash value=28 text=(t "bookLibrary.components.dateRange.addTimeValue" days=28))
            }}
            @value={{twoDate.durationDays}}
            @onSelect={{twoDate.setAbsoluteDays}}
          />
        </fieldset>
        <fieldset class="basis-full space-y-1">
          <label for="{{uniqueId}}-end" class="block">{{t "bookLibrary.components.dateRange.endDate"}}</label>
          <TwoDatePickerInput
            id="{{uniqueId}}-end"
            name="end-date"
            @date={{@endDate}}
            @onDateSelect={{twoDate.end.onDateSelect}}
            @min={{twoDate.end.min}}
            @max={{twoDate.end.max}}
            @themeKey={{@themeKey}}
          />
        </fieldset>
      </TwoDatePicker>
    {{/let}}
  </div>
</template>

export default BookLibraryDateRange
